import { useCallback, useMemo, useState } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import {
    formatDateWithTimezone,
    formatTimeAndTimezone,
} from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    DetailedStatus,
    InfoTooltip,
    pendo,
    Svg,
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
    Tooltip,
} from "@impero-com/ui";
import {
    COLUMN_FILTERS_PARAM_NAME,
    FilterTarget,
    usePresetTableSearchParams,
    useTableStateStorage,
    controlActivitiesListColumnFiltersCodecMap,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS as COLUMN_ID,
} from "@impero-com/ui/domain";
import ArchiveIcon from "@impero-com/ui/img/archive.svg";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedUniqueValues,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";
import moment, { Moment } from "moment-timezone";

import {
    ExistingControlResultPaginatedListDto as ControlResultPaginatedListDto,
    ExistingControlResult,
    CategoryAndTag,
    Friction,
} from "api/generated/model";
import { ControlResultOverviewAction } from "api/generated/model/controlResultOverviewAction";
import { UserTagCategoryOverview } from "api/generated/model/userTagCategoryOverview";
import { ClampedText } from "components/clampedText/ClampedText";
import { FilterBarDateTimeRange } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRange";
import { DateTimeRangeFilterValue } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeTypes";
import { dateTimeRangeTSTFilterFn } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeUtils";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidStringTagsFilter } from "components/table/tsTable/TSTValidStringTagsFilter";
import { TagAndCategoryListFromRecord } from "components/tag/tagAndCategory/tagAndCategoryListFromRecord";
import { LegacyRoute } from "legacyRoutes";
import { tagsTSTSortingFn } from "org/tags/tagsTSTUtils";
import {
    computeEvent,
    localizeEvent,
} from "utils/controlResults/activityStatusUtils";
import { getPerformerName } from "utils/controlResults/actorName";
import { buildLatestEntitySnapshotOptions } from "utils/controlResults/entitySnapshotOptions";
import { getStepActorName } from "utils/controlResults/getStepActorName";
import {
    buildLookupContext,
    LookupContext,
} from "utils/controlResults/lookupContext";
import { useCurrentUserTimezone } from "utils/currentTimezone";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import {
    createFilterFnWithMode,
    createContainsOnlyFilterFn,
} from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";
import { matchesCategoryAndTagFilterFn } from "utils/tags/matchesCategoryAndTagFilterFn";
import {
    dropUndefinedTags,
    formatTagsRecordToString,
} from "utils/tags/tagRecord";

import { StatusCascader } from "./components/StatusCascader";
import { TimeToDueDateFilter } from "./components/TimeToDueDateFilter";
import {
    buildControlActivitiesListDefaultState,
    buildControlActivitiesListTableStateStorageKey,
    CATEGORY_COLUMN_PREFIX,
    CONTROL_ACTIVITIES_PENDO,
    ControlActivitiesScope,
} from "./consts";
import * as theme from "./ControlActivitiesList.css";
import {
    CONTROL_PROGRAM_STATES,
    ControlProgramState,
    toControlProgramState,
} from "./controlProgramStatusFilter";
import {
    FRICTION_TYPE_FILTER_KEYS,
    getFrictionTypeFilterKeys,
} from "./frictionTypeFilter";
import { HEALTH_FILTER_KEYS, HealthFilterKey } from "./healthFilter";
import {
    ControlActivitiesListColumnFilters,
    ControlActivityListColumnId,
    ControlActivityTableRow,
} from "./types";
import { getActiveCategoryNames, formatCategoryTags } from "./utils/category";
import {
    getClassificationClasses,
    localizeClassificationClass,
    PSEUDO_CLASS_TOKENS,
} from "./utils/classification";
import {
    buildCoveredPeriodTechString,
    buildSortedPeriodFilterOptions,
    compareCoveredPeriods,
    formatCoveredPeriod,
} from "./utils/coveredPeriod";
import { computeDueDateBounds } from "./utils/dueDateBounds";
import {
    buildHealthDetails,
    buildHealthLabels,
    buildHealthStatus,
} from "./utils/health";
import { matchesStatusFilter } from "./utils/statusFilterOptions";
import {
    computeDefaultClaimant,
    computeDueDate,
    getResponsibleOrReviewerNames,
    includesSomeFilterFn,
    localizeArchived,
    localizeControlProgramStatus,
    localizePerformerName,
} from "./utils/tableColumnHelpers";
import {
    buildLatestControlTitleOptions,
    localizeColumnFilterPlaceholder,
} from "./utils/tableFilterHelpers";
import {
    formatTimeToDueDate,
    matchesTimeToDueDate,
    timeToDueDate,
} from "./utils/timeToDueDate";

/**
 * @param scope Which activities the list is showing — it gets its own persisted view.
 */
export function useControlActivitiesListTable(
    controlResultsDto: ControlResultPaginatedListDto,
    tagCategories: UserTagCategoryOverview[],
    onOpenTimeline: (row: ControlActivityTableRow) => void,
    scope: ControlActivitiesScope
): {
    table: ReturnType<typeof useReactTable<ControlActivityTableRow>>;
    getFilterComponent: (
        column: Column<ControlActivityTableRow>,
        target: FilterTarget
    ) => React.ReactNode;
    canAddException: (column: Column<ControlActivityTableRow>) => boolean;
    tableId: string;
    /** Drops the column-filter URL param so the table reverts to no filters. */
    clearColumnFilters: (params: URLSearchParams) => void;
    /** Wipes the persisted column filters so they don't replay on re-entry. */
    resetPersistedColumnFilters: () => void;
    /**
     * The due-date span of the rows on screen, for showing which period is
     * displayed while nothing bounds the due date. `null` when there are none.
     */
    dueDateBounds: [string, string] | null;
} {
    const { t } = useTranslation();
    const timezoneName = useCurrentUserTimezone();
    const [now] = useState(Date.now);

    const healthEnabled = useFeatureFlag("controlHealthScoreActivities");
    const entitiesEnabled = useFeatureFlag("controlEntities");
    const classificationTitle =
        controlResultsDto.linkedResources.classification?.title ?? null;
    const staticColumns = useMemo(
        () =>
            buildColumns(
                t,
                timezoneName,
                onOpenTimeline,
                healthEnabled,
                entitiesEnabled,
                classificationTitle,
                now
            ),
        [
            t,
            timezoneName,
            onOpenTimeline,
            healthEnabled,
            entitiesEnabled,
            classificationTitle,
            now,
        ]
    );
    const categoryNames = useMemo(
        () => getActiveCategoryNames(tagCategories),
        [tagCategories]
    );
    const dynamicCategoryColumns = useMemo(
        () => categoryNames.map((name) => buildCategoryTagsColumn(name)),
        [categoryNames]
    );
    const columns = useMemo(
        () => [...staticColumns, ...dynamicCategoryColumns],
        [staticColumns, dynamicCategoryColumns]
    );

    const tableData: ControlActivityTableRow[] = useMemo(() => {
        return buildControlActivities(controlResultsDto, moment(now));
    }, [controlResultsDto, now]);

    const notificationLabel = t("Table_RemovedFiltersWarning");

    const tableId = buildControlActivitiesListTableStateStorageKey(scope);

    const stateAndOnChanges = usePresetTableSearchParams(
        tableId,
        buildControlActivitiesListDefaultState(categoryNames, scope),
        controlActivitiesListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );

    const [, setStoredTableState] = useTableStateStorage(tableId);
    const clearColumnFilters = useCallback(
        (params: URLSearchParams) => params.delete(COLUMN_FILTERS_PARAM_NAME),
        []
    );
    const resetPersistedColumnFilters = useCallback(
        () => setStoredTableState((prev) => ({ ...prev, columnFilters: [] })),
        [setStoredTableState]
    );

    const table = useReactTable({
        state: {
            ...stateAndOnChanges.state,
            columnVisibility: {
                ...stateAndOnChanges.state.columnVisibility,
                [COLUMN_ID.FRICTION_TYPE]: false,
            },
        },
        onStateChange: stateAndOnChanges.onStateChange,
        data: tableData,
        columns,
        enableRowSelection: true,
        // Key the row selection on the activity, not on the row's position in
        // the fetched data.
        getRowId: (row) => row.activityId,
        initialState: {
            // useTableSearchParams doesn't support all initial states - supported states should be declared only there
            // columnPinning is seeded (from the URL or its default) by the preset hook's initialState.
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        defaultColumn: {
            size: TST_COLUMN_SIZE.S,
        },
        enablePinning: true,
        enableColumnPinning: true,
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ControlActivityTableRow>(),
        getSortedRowModel: getSortedRowModel<ControlActivityTableRow>(),
        getFacetedMinMaxValues:
            getFacetedMinMaxValues<ControlActivityTableRow>(),
        getFacetedUniqueValues:
            getFacetedUniqueValues<ControlActivityTableRow>(),
        getFilteredRowModel: getFilteredRowModel<ControlActivityTableRow>(),
        getPaginationRowModel: getPaginationRowModel<ControlActivityTableRow>(),
    });

    const getFilterComponentWrapper = (
        column: Column<ControlActivityTableRow>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target);

    // The faceted values are memoized against the row model the table computed to
    // render, so they keep their identity until the rows change — which lets this
    // skip the scan entirely on a re-render that didn't touch them.
    const facetedDueDates = table
        .getColumn(COLUMN_ID.DUE_DATE)
        ?.getFacetedUniqueValues();
    const dueDateBounds = useMemo(
        () => computeDueDateBounds(facetedDueDates, timezoneName),
        [facetedDueDates, timezoneName]
    );

    return {
        table,
        getFilterComponent: getFilterComponentWrapper,
        canAddException: (column: Column<ControlActivityTableRow>) =>
            column.id === COLUMN_ID.TAGS,
        tableId,
        clearColumnFilters,
        resetPersistedColumnFilters,
        dueDateBounds,
    };
}

const columnHelper = createColumnHelper<ControlActivityTableRow>();

function buildClassificationClassColumn(
    t: TFunction,
    classificationTitle: string
) {
    return columnHelper.accessor(
        (row) =>
            row.classificationClass
                .map((className) => localizeClassificationClass(className, t))
                .join(", "),
        {
            id: COLUMN_ID.CLASSIFICATION_CLASS,
            header: classificationTitle,
            cell: (info) => (
                <span className={theme.breakWord}>{info.getValue()}</span>
            ),
            enableColumnFilter: true,
            enableSorting: true,
            enableHiding: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                includesSomeFilterFn(row.original.classificationClass, value)
            ),
        }
    );
}

function buildEntityColumn(t: TFunction) {
    return columnHelper.accessor((row) => row.entity?.name ?? "", {
        id: COLUMN_ID.ENTITY,
        header: t("ControlActivitiesList_Entity_Header"),
        cell: (info) => (
            <span className={theme.breakWord}>{info.getValue()}</span>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        enableHiding: true,
        sortingFn: "alphanumericCaseSensitive",
        filterFn: createFilterFnWithMode((row, value: string[]) => {
            // Filter by control entity id so the deleted/renamed entities stay filterable
            const entityId = row.original.entity?.id;
            return (
                entityId !== undefined && value.includes(entityId.toString())
            );
        }),
    });
}

// Buckets by friction alone: an open no-friction activity is "clean" here even
// though its cell reads "In progress", because status is deliberately not part
// of this filter.
function getHealthFilterKey(row: ControlActivityTableRow): HealthFilterKey {
    switch (row.frictionData.friction) {
        case Friction.HighFriction:
            return HEALTH_FILTER_KEYS.HIGH_FRICTION;
        case Friction.Friction:
            return HEALTH_FILTER_KEYS.FRICTION;
        case Friction.NoFriction:
            return HEALTH_FILTER_KEYS.CLEAN;
    }
}

// Ordered by status rather than alphabetically
function buildControlProgramStatusFilterOptions(
    t: TFunction
): Array<{ label: string; value: ControlProgramState }> {
    return [
        {
            value: CONTROL_PROGRAM_STATES.ACTIVE,
            label: t("ControlActivitiesList_ControlProgramStatus_Active"),
        },
        {
            value: CONTROL_PROGRAM_STATES.INACTIVE,
            label: t("ControlActivitiesList_ControlProgramStatus_Inactive"),
        },
        {
            value: CONTROL_PROGRAM_STATES.DELETED,
            label: t("ControlActivitiesList_ControlProgramStatus_Deleted"),
        },
    ];
}

// Fixed option list for the Health filter, reusing the cell's existing copy.
// Order matches the cell's severity (high friction → clean).
function buildHealthFilterOptions(
    t: TFunction
): Array<{ label: string; value: HealthFilterKey }> {
    return [
        {
            value: HEALTH_FILTER_KEYS.HIGH_FRICTION,
            label: t("ControlResult_Friction_HighFriction"),
        },
        {
            value: HEALTH_FILTER_KEYS.FRICTION,
            label: t("ControlResult_Friction_Friction"),
        },
        {
            value: HEALTH_FILTER_KEYS.CLEAN,
            label: t("ControlResult_Health_Clean"),
        },
    ];
}

function buildFrictionTypeFilterColumn(t: TFunction) {
    return columnHelper.accessor(getFrictionTypeFilterKeys, {
        id: COLUMN_ID.FRICTION_TYPE,
        header: t("ControlActivitiesList_FrictionType_Header"),
        meta: { filterOnly: true },
        enableColumnFilter: true,
        enableHiding: false,
        enablePinning: false,
        enableSorting: false,
        filterFn: createFilterFnWithMode<ControlActivityTableRow, string[]>(
            (row, value) =>
                includesSomeFilterFn(
                    getFrictionTypeFilterKeys(row.original),
                    value
                )
        ),
    });
}

function buildHealthColumn(
    t: TFunction,
    onOpenTimeline: (row: ControlActivityTableRow) => void
) {
    return columnHelper.accessor((row) => row.frictionData.sorting_weight, {
        id: COLUMN_ID.HEALTH,
        header: () => (
            <span className={theme.healthHeader}>
                {t("ControlActivitiesList_Health_Header")}
                <InfoTooltip
                    size="small"
                    text={t("ControlActivitiesList_Health_HeaderInfo")}
                />
            </span>
        ),
        meta: {
            // Pendo adoption tracking: sort clicks on the header cell.
            headerPendoId: CONTROL_ACTIVITIES_PENDO.healthHeader,
            // The info tooltip belongs in the table head only; the filter bar
            // names the column with this plain label instead.
            plainLabel: t("ControlActivitiesList_Health_Header"),
        },
        size: TST_COLUMN_SIZE.L,
        enableColumnFilter: true,
        // Client-side "standard filter": match the row's Health bucket against
        // the selected values (the data is already loaded, like every other
        // column filter here).
        filterFn: createFilterFnWithMode<ControlActivityTableRow, string[]>(
            (row, value) =>
                includesSomeFilterFn([getHealthFilterKey(row.original)], value)
        ),
        enableSorting: true,
        sortingFn: "alphanumeric",
        enableHiding: true,
        cell: (info) => {
            const row = info.row.original;
            const status = buildHealthStatus(row.frictionData);
            const labels = buildHealthLabels(row.frictionData, t);
            const details = buildHealthDetails(row, t);
            return (
                // Pendo adoption tracking: the cell itself, and the clickable
                // status as the timeline-modal trigger.
                <div {...pendo(CONTROL_ACTIVITIES_PENDO.healthCell)}>
                    <DetailedStatus
                        className={theme.healthCell}
                        status={status}
                        labels={labels}
                        details={details}
                        onClick={() => onOpenTimeline(info.row.original)}
                        {...pendo(CONTROL_ACTIVITIES_PENDO.timelineOpen)}
                    />
                </div>
            );
        },
    });
}

function DateTimeCell({
    date,
    timezoneName,
}: {
    date: string | null | undefined;
    timezoneName: string;
}) {
    if (!date) {
        return null;
    }
    const timezone = { name: timezoneName };
    return (
        <span>
            {formatDateWithTimezone(date, timezone)}
            <br />
            {formatTimeAndTimezone(date, timezone)}
        </span>
    );
}

function buildColumns(
    t: TFunction,
    timezoneName: string,
    onOpenTimeline: (row: ControlActivityTableRow) => void,
    includeHealth: boolean,
    includeEntity: boolean,
    classificationTitle: string | null,
    now: number
) {
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => {
                return (
                    <Checkbox
                        checked={info.table.getIsAllPageRowsSelected()}
                        indeterminate={info.table.getIsSomePageRowsSelected()}
                        onChange={info.table.getToggleAllRowsSelectedHandler()}
                    />
                );
            },
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => {
                const onClick = () => {
                    info.row.toggleSelected();
                };
                return (
                    <Checkbox
                        checked={info.row.getIsSelected()}
                        onClick={onClick}
                    />
                );
            },
        }),
        columnHelper.accessor((row) => row.controlProgramLabel, {
            id: COLUMN_ID.CONTROL_PROGRAM_ID,
            header: t("ControlResult_ControlProgram"),
            size: TST_COLUMN_SIZE.L,
            cell: (info) => <ClampedText text={info.getValue()} />,
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[]) => {
                // Filter by control program id so the deleted/renamed programs stay filterable
                const cpId = row.original.controlProgramId;
                return value.includes(cpId.toString());
            }),
        }),
        columnHelper.accessor(
            (row) => localizeControlProgramStatus(row.controlProgramState, t),
            {
                id: COLUMN_ID.CONTROL_PROGRAM_STATUS,
                header: t("ControlActivitiesList_ControlProgramStatus_Header"),
                size: TST_COLUMN_SIZE.M,
                cell: (info) => <span>{info.getValue()}</span>,
                enableColumnFilter: true,
                enableSorting: true,
                sortingFn: "alphanumericCaseSensitive",
                filterFn: createFilterFnWithMode<
                    ControlActivityTableRow,
                    ControlProgramState[]
                >((row, value) =>
                    value.includes(row.original.controlProgramState)
                ),
            }
        ),
        columnHelper.accessor((row) => row.title, {
            id: COLUMN_ID.CONTROL_ID,
            header: t("ControlResult_Title"),
            size: TST_COLUMN_SIZE.L,
            enableHiding: false,
            cell: (info) => {
                const { activityId, archived, archivedOn } = info.row.original;
                const archivedLabel = archivedOn
                    ? t("ControlActivitiesList_ArchivedOn_Tooltip", {
                          date: formatDateWithTimezone(archivedOn, {
                              name: timezoneName,
                          }),
                      })
                    : t("ControlResult_Archived");
                return (
                    <span className={theme.controlCell}>
                        <a href={`${LegacyRoute.CONTROL_RESULT}/${activityId}`}>
                            <ClampedText text={info.getValue()} />
                        </a>
                        {archived && (
                            <Tooltip title={archivedLabel} noWrapper>
                                <span
                                    tabIndex={0}
                                    role="img"
                                    aria-label={archivedLabel}
                                    className={theme.archivedIcon}
                                >
                                    <Svg src={ArchiveIcon} />
                                </span>
                            </Tooltip>
                        )}
                    </span>
                );
            },
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[]) => {
                // Filter by control id so deleted/renamed controls stay filterable
                return value.includes(row.original.controlId);
            }),
        }),
        ...(includeEntity ? [buildEntityColumn(t)] : []),
        columnHelper.accessor((row) => row.description ?? "", {
            id: COLUMN_ID.DESCRIPTION,
            header: t("ControlResult_Description"),
            size: TST_COLUMN_SIZE.L,
            cell: (info) => {
                return <span>{info.getValue()}</span>;
            },
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createContainsOnlyFilterFn(),
        }),
        columnHelper.accessor(
            (row) =>
                formatCoveredPeriod(t, row.coveredPeriod, row.financialYear) ??
                "",
            {
                id: COLUMN_ID.PERIOD,
                header: t("ControlResult_CoveredPeriod"),
                size: TST_COLUMN_SIZE.L,
                cell: (info) => {
                    return <span>{info.getValue()}</span>;
                },
                enableColumnFilter: true,
                enableSorting: true,
                sortingFn: (rowA, rowB) =>
                    compareCoveredPeriods(
                        rowA.original.coveredPeriod,
                        rowB.original.coveredPeriod
                    ),
                // Matches the locale-independent tech string rather than the
                // translated cell value, so a shared link survives a language change
                filterFn: createFilterFnWithMode(
                    (row, value: string[] | null) => {
                        const techString = row.original.periodTechString;
                        return (
                            techString !== null &&
                            (value ?? []).includes(techString)
                        );
                    }
                ),
            }
        ),
        columnHelper.accessor((row) => formatTagsRecordToString(row.tags), {
            id: COLUMN_ID.TAGS,
            header: t("ControlResult_Tags"),
            size: TST_COLUMN_SIZE.L,
            cell: (info) => (
                <TagAndCategoryListFromRecord tags={info.row.original.tags} />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: (rowA, rowB) =>
                tagsTSTSortingFn(
                    Object.values(rowA.original.tags).flat(),
                    Object.values(rowB.original.tags).flat()
                ),
            filterFn: createFilterFnWithMode((row, value: CategoryAndTag[]) =>
                matchesCategoryAndTagFilterFn(row.original.tags, value)
            ),
        }),
        columnHelper.accessor((row) => row.created, {
            id: COLUMN_ID.CREATED,
            header: t("ControlResult_Created"),

            cell: (info) => (
                <DateTimeCell
                    date={info.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "datetime",
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(value, row.original.created)
            ),
        }),
        //completionDueDate => responsible due date
        columnHelper.accessor((row) => row.completionDueDate, {
            id: COLUMN_ID.COMPLETION_DUE_DATE,
            header: t("ControlResult_CompletionDueDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "datetime",
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(
                        value,
                        row.original.completionDueDate
                    )
            ),
        }),
        columnHelper.accessor((row) => row.firstReviewerDueDate ?? "", {
            id: COLUMN_ID.FIRST_REVIEWER_DUE_DATE,
            header: t("ControlResult_FirstReviewerDueDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => row.original.firstReviewerDueDate,
                Cmp.undefinedLast()
            ),
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(
                        value,
                        row.original.firstReviewerDueDate
                    )
            ),
        }),
        columnHelper.accessor((row) => row.secondReviewerDueDate ?? "", {
            id: COLUMN_ID.SECOND_REVIEWER_DUE_DATE,
            header: t("ControlResult_SecondReviewerDueDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => row.original.secondReviewerDueDate,
                Cmp.undefinedLast()
            ),
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(
                        value,
                        row.original.secondReviewerDueDate
                    )
            ),
        }),
        columnHelper.accessor((row) => row.dueDate, {
            id: COLUMN_ID.DUE_DATE,
            header: t("ControlResult_DueDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            // Due date is filtered by the backend via the external date
            // filters, so the column is sortable but not filterable in-table.
            enableColumnFilter: false,
            enableSorting: true,
            sortingFn: "datetime",
        }),
        columnHelper.accessor(
            (row) =>
                timeToDueDate(
                    row.dueDate,
                    row.completedAndReviewedDate !== null,
                    now
                ),
            {
                id: COLUMN_ID.TIME_TO_DUE_DATE,
                header: t("ControlActivitiesList_TimeToDueDate_Header"),
                enableColumnFilter: true,
                filterFn: (
                    row,
                    columnId,
                    value: ControlActivitiesListColumnFilters["timeToDueDate"]
                ) =>
                    matchesTimeToDueDate(
                        row.getValue<number | null>(columnId),
                        value?.value ?? null
                    ),
                enableSorting: true,
                sortingFn: Cmp.map(
                    (row) =>
                        row.getValue<number | null>(COLUMN_ID.TIME_TO_DUE_DATE),
                    Cmp.undefinedLast()
                ),
                cell: (info) => {
                    const duration = info.getValue();
                    return duration === null ? null : (
                        <span
                            className={duration < 0 ? theme.overdue : undefined}
                        >
                            {formatTimeToDueDate(duration, t)}
                        </span>
                    );
                },
            }
        ),
        columnHelper.accessor((row) => row.completedDate ?? "", {
            id: COLUMN_ID.COMPLETED_DATE,
            header: t("ControlResult_SubmittedDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => row.original.completedDate,
                Cmp.undefinedLast()
            ),
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(value, row.original.completedDate)
            ),
        }),
        columnHelper.accessor((row) => row.firstReviewedDate ?? "", {
            id: COLUMN_ID.FIRST_REVIEWED_DATE,
            header: t("ControlResult_FirstReviewedDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => row.original.firstReviewedDate,
                Cmp.undefinedLast()
            ),
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(
                        value,
                        row.original.firstReviewedDate
                    )
            ),
        }),
        columnHelper.accessor((row) => row.secondReviewedDate ?? "", {
            id: COLUMN_ID.SECOND_REVIEWED_DATE,
            header: t("ControlResult_SecondReviewedDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => row.original.secondReviewedDate,
                Cmp.undefinedLast()
            ),
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(
                        value,
                        row.original.secondReviewedDate
                    )
            ),
        }),
        columnHelper.accessor((row) => row.reviewedDate ?? "", {
            id: COLUMN_ID.REVIEWED_DATE,
            header: t("ControlResult_ReviewedDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => row.original.reviewedDate,
                Cmp.undefinedLast()
            ),
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(value, row.original.reviewedDate)
            ),
        }),
        columnHelper.accessor((row) => row.completedAndReviewedDate ?? "", {
            id: COLUMN_ID.COMPLETED_AND_REVIEWED_DATE,
            header: t("ControlResult_CompletedDate"),

            cell: (cellContext) => (
                <DateTimeCell
                    date={cellContext.getValue()}
                    timezoneName={timezoneName}
                />
            ),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => row.original.completedAndReviewedDate,
                Cmp.undefinedLast()
            ),
            filterFn: createFilterFnWithMode(
                (row, value: DateTimeRangeFilterValue) =>
                    dateTimeRangeTSTFilterFn(
                        value,
                        row.original.completedAndReviewedDate
                    )
            ),
        }),
        columnHelper.accessor((row) => row.responsible, {
            id: COLUMN_ID.RESPONSIBLE,
            header: t("ControlResult_Assignee"),
            cell: (info) => {
                return <span>{info.getValue()}</span>;
            },
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, COLUMN_ID.RESPONSIBLE, value)
            ),
        }),

        columnHelper.accessor((row) => row.claimant, {
            id: COLUMN_ID.CLAIMANT,
            header: t("ControlResult_Claimant"),
            cell: (info) => {
                return <span>{info.getValue()}</span>;
            },
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, COLUMN_ID.CLAIMANT, value)
            ),
        }),

        columnHelper.accessor(
            (row) => localizePerformerName(row.performer, t),
            {
                id: COLUMN_ID.PERFORMER,
                header: t("ControlResult_Performer"),
                cell: (info) => {
                    return <span>{info.getValue()}</span>;
                },
                enableColumnFilter: true,
                enableSorting: true,
                sortingFn: "alphanumericCaseSensitive",
                filterFn: createFilterFnWithMode(
                    (row, value: string[] | null) =>
                        tstArrIncludesSomeFilterFn(
                            row,
                            COLUMN_ID.PERFORMER,
                            value
                        )
                ),
            }
        ),
        columnHelper.accessor((row) => row.reviewer, {
            id: COLUMN_ID.REVIEWER,
            header: t("ControlResult_Reviewer"),
            cell: (info) => {
                return <span>{info.getValue()}</span>;
            },
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, COLUMN_ID.REVIEWER, value)
            ),
        }),
        columnHelper.accessor(
            (row) => localizePerformerName(row.reviewPerformer, t),
            {
                id: COLUMN_ID.REVIEW_PERFORMER,
                header: t("ControlResult_ReviewPerformer"),
                cell: (info) => {
                    return <span>{info.getValue()}</span>;
                },
                enableColumnFilter: true,
                enableSorting: true,
                sortingFn: "alphanumericCaseSensitive",
                filterFn: createFilterFnWithMode(
                    (row, value: string[] | null) =>
                        tstArrIncludesSomeFilterFn(
                            row,
                            COLUMN_ID.REVIEW_PERFORMER,
                            value
                        )
                ),
            }
        ),
        columnHelper.accessor((row) => row.secondReviewer, {
            id: COLUMN_ID.SECOND_REVIEWER,
            header: t("ControlResult_SecondReviewer"),
            cell: (info) => {
                return <span>{info.getValue()}</span>;
            },
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(
                    row,
                    COLUMN_ID.SECOND_REVIEWER,
                    value
                )
            ),
        }),
        columnHelper.accessor(
            (row) => localizePerformerName(row.secondReviewPerformer, t),
            {
                id: COLUMN_ID.SECOND_REVIEW_PERFORMER,
                header: t("ControlResult_SecondReviewPerformer"),
                cell: (info) => {
                    return <span>{info.getValue()}</span>;
                },
                enableColumnFilter: true,
                enableSorting: true,
                sortingFn: "alphanumericCaseSensitive",
                filterFn: createFilterFnWithMode(
                    (row, value: string[] | null) =>
                        tstArrIncludesSomeFilterFn(
                            row,
                            COLUMN_ID.SECOND_REVIEW_PERFORMER,
                            value
                        )
                ),
            }
        ),
        // user column
        columnHelper.accessor(
            (row) => getResponsibleOrReviewerNames(row, t).join(", "),
            {
                id: COLUMN_ID.RESPONSIBLE_OR_REVIEWER,
                header: t("ControlResult_AssigneeOrReviewer"),
                size: TST_COLUMN_SIZE.L,
                cell: (info) => {
                    return <span>{info.getValue()}</span>;
                },
                enableColumnFilter: true,
                enableSorting: true,
                sortingFn: "alphanumericCaseSensitive",
                filterFn: createFilterFnWithMode(
                    (row, value: string[] | null) =>
                        includesSomeFilterFn(
                            getResponsibleOrReviewerNames(row.original, t),
                            value
                        )
                ),
            }
        ),
        columnHelper.accessor((row) => localizeEvent(row.workflowStatus, t), {
            id: COLUMN_ID.WORKFLOW_STATUS,
            header: t("ControlResult_Status"),
            size: TST_COLUMN_SIZE.L,
            cell: (info) => {
                return <span>{info.getValue()}</span>;
            },
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode<ControlActivityTableRow, string[]>(
                (row, value) =>
                    matchesStatusFilter(row.original.workflowStatus, value)
            ),
        }),
        ...(includeHealth
            ? [
                  buildHealthColumn(t, onOpenTimeline),
                  buildFrictionTypeFilterColumn(t),
              ]
            : []),
        ...(classificationTitle !== null
            ? [buildClassificationClassColumn(t, classificationTitle)]
            : []),
        columnHelper.accessor((row) => localizeArchived(row.archived, t), {
            id: COLUMN_ID.ARCHIVED,
            header: t("ControlResult_Archived"),
            cell: (info) => {
                return <span>{info.getValue()}</span>;
            },
            enableColumnFilter: false,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
        }),
    ];
}

/**
 * Builds a dynamic column for a single tag category.
 * Each column shows the tags in that category for each row.
 */
function buildCategoryTagsColumn(categoryName: string) {
    const columnId = `${CATEGORY_COLUMN_PREFIX}${categoryName}`;
    return columnHelper.accessor((row) => row.tags[categoryName] ?? [], {
        id: columnId,
        header: categoryName,
        cell: (info) => {
            const value = formatCategoryTags(info.getValue());
            return value ? <span>{value}</span> : null;
        },
        // The tags filter already covers every category, so these columns are
        // display-only — a filter per category would just duplicate it.
        enableColumnFilter: false,
        enableSorting: true,
        // Sorts by tag count, then alphabetically (case-insensitive).
        sortingFn: (rowA, rowB) =>
            tagsTSTSortingFn(
                rowA.original.tags[categoryName] ?? [],
                rowB.original.tags[categoryName] ?? []
            ),
    });
}

export function buildControlActivities(
    controlResultsDto: ControlResultPaginatedListDto,
    now: Moment
): ControlActivityTableRow[] {
    const lookupContext = buildLookupContext(controlResultsDto);
    return controlResultsDto.content.map((crDto) =>
        buildControlActivity(crDto.resource, crDto.actions, lookupContext, now)
    );
}

function buildControlActivity(
    controlResult: ExistingControlResult,
    action: ControlResultOverviewAction,
    lookupContext: LookupContext,
    now: Moment
): ControlActivityTableRow {
    const {
        id,
        controlProgramId,
        controlId,
        title,
        coveredPeriod,
        financialYear,
        created,
        tags,
        stepGroup,
        archived,
        archivedOn,
        segregationOfDutiesEnabled,
        isOverdue,
        rejectionCount,
        reopenCount,
        frictionData,
    } = controlResult;
    const cp = lookupContext.controlProgramsById.get(controlProgramId);
    // A deleted program has no name left to show, so the column falls back to its id.
    const controlProgramLabel = cp ? cp.name : controlProgramId.toString();
    const controlProgramState = toControlProgramState(cp);

    const control = lookupContext.controlsById.get(controlId);
    const description = control ? control.description : null;
    const event = computeEvent(controlResult, now);
    const dueDate = computeDueDate(controlResult);
    const initialStep = stepGroup.initialStep;
    const firstReviewerStep = stepGroup.subsequentSteps[0];
    const secondReviewerStep = stepGroup.subsequentSteps[1];

    // Drives the reassign segregation-of-duties check.
    const stepAssigneeUserIds = [
        initialStep,
        ...(firstReviewerStep ? [firstReviewerStep] : []),
        ...(secondReviewerStep ? [secondReviewerStep] : []),
    ].map((step) => {
        if (!step.performance) {
            return step.kind.type === "User" ? step.kind.userId : null;
        }
        const { performer } = step.performance;
        switch (performer.type) {
            case "User":
                return performer.userId;
            case "System":
                return null;
            default:
                return assertNever(performer);
        }
    });

    const responsible = getStepActorName(initialStep, lookupContext);
    const reviewer = firstReviewerStep
        ? getStepActorName(firstReviewerStep, lookupContext)
        : null;
    const secondReviewer = secondReviewerStep
        ? getStepActorName(secondReviewerStep, lookupContext)
        : null;

    // Performer = who actually performed a step: a user or the Impero system.
    // This may differ from the assigned actor, e.g. when the assignee is a pool.
    const performer = getPerformerName(
        initialStep.performance ?? null,
        lookupContext
    );
    const reviewPerformer = getPerformerName(
        firstReviewerStep?.performance ?? null,
        lookupContext
    );
    const secondReviewPerformer = getPerformerName(
        secondReviewerStep?.performance ?? null,
        lookupContext
    );

    // Reviewed dates from each reviewer step's performance
    const firstReviewedDate =
        firstReviewerStep?.performance?.performedAt ?? null;
    const secondReviewedDate =
        secondReviewerStep?.performance?.performedAt ?? null;

    // reviewedDate = when the final reviewer approved (last subsequent step with performance)
    const finalReviewerStep = secondReviewerStep ?? firstReviewerStep;
    const reviewedDate = finalReviewerStep?.performance?.performedAt ?? null;

    // completedAndReviewedDate = the performedAt of the very last step that was completed
    // (i.e. the final step in the chain, only set when all steps are done)
    const allSteps = [
        initialStep,
        ...(firstReviewerStep ? [firstReviewerStep] : []),
        ...(secondReviewerStep ? [secondReviewerStep] : []),
    ];
    const allPerformed = allSteps.every((step) => !!step.performance);
    const completedAndReviewedDate = allPerformed
        ? (allSteps[allSteps.length - 1].performance?.performedAt ?? null)
        : null;

    const claimant =
        computeDefaultClaimant(controlResult, lookupContext) ?? null;

    return {
        activityId: id,
        controlId,
        controlProgramId,
        controlProgramLabel,
        controlProgramState,
        title,
        description: description ?? null,
        coveredPeriod: coveredPeriod ?? null,
        financialYear: financialYear ?? null,
        periodTechString: coveredPeriod
            ? buildCoveredPeriodTechString(coveredPeriod, financialYear ?? null)
            : null,
        tags: dropUndefinedTags(tags),
        entity: controlResult.entity ?? null,
        created, // notification date column
        completionDueDate: initialStep.dueDate, // responsible due date column
        firstReviewerDueDate: firstReviewerStep?.dueDate ?? null,
        secondReviewerDueDate: secondReviewerStep?.dueDate ?? null,
        firstReviewedDate,
        secondReviewedDate,
        dueDate,
        completedDate: initialStep.performance?.performedAt ?? null, // submitted column
        completedAndReviewedDate, // final reviewed column
        responsible,
        claimant,
        performer,
        reviewer,
        reviewPerformer,
        secondReviewer,
        secondReviewPerformer,
        reviewedDate,
        workflowStatus: event.eventType,
        isOverdue,
        rejectionCount,
        reopenCount,
        frictionData,
        classificationClass: getClassificationClasses(
            controlResult.classification,
            lookupContext.classificationClassNamesById
        ),
        archived,
        archivedOn: archivedOn ?? null,
        canEditProgram: action.edit,
        reviewerCount: stepGroup.subsequentSteps.length,
        segregationOfDutiesEnabled,
        stepAssigneeUserIds,
    };
}

function getFilterComponent(
    t: TFunction,
    column: Column<ControlActivityTableRow>,
    target: FilterTarget
): React.ReactNode {
    const getFilterHelpers =
        createFilterHelpersGetter<ControlActivitiesListColumnFilters>(column);
    const columnId = column.id as ControlActivityListColumnId;

    switch (columnId) {
        case COLUMN_ID.TIME_TO_DUE_DATE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TimeToDueDateFilter value={value} onChange={setFilterValue} />
            );
        }
        case COLUMN_ID.CONTROL_PROGRAM_ID: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const idToLabel = new Map<string, string>();
            for (const row of column.getFacetedRowModel().flatRows) {
                idToLabel.set(
                    row.original.controlProgramId.toString(),
                    row.original.controlProgramLabel
                );
            }

            const options = idToLabel
                .entries()
                .map(([cpId, label]) => ({
                    label,
                    value: cpId,
                    customLabel: <CustomEllipsisLabel text={label} />,
                }))
                .toArray();
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t(
                        "ControlActivitiesList_ControlProgramFilter_Placeholder"
                    )}
                    dropdownMatchSelectWidth={500}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.CONTROL_PROGRAM_STATUS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={buildControlProgramStatusFilterOptions(t)}
                    placeholder={t(
                        "ControlActivitiesList_ControlProgramStatus_Placeholder"
                    )}
                    value={value}
                    setFilterValue={setFilterValue}
                    // Keep the status order (active → deleted).
                    presorted
                />
            );
        }
        case COLUMN_ID.CONTROL_ID: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const latestOptions = buildLatestControlTitleOptions(
                column.getFacetedRowModel().flatRows
            ).map(({ controlId, title }) => ({
                label: title,
                value: controlId,
                customLabel: <CustomEllipsisLabel text={title} />,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={latestOptions}
                    placeholder={t(
                        "ControlActivitiesList_ControlFilter_Placeholder"
                    )}
                    dropdownMatchSelectWidth={500}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.PERIOD: {
            const options = buildSortedPeriodFilterOptions(
                column.getFacetedRowModel().flatRows.map((row) => ({
                    techString: row.original.periodTechString ?? "",
                    label: row.getValue<string>(COLUMN_ID.PERIOD),
                    period: row.original.coveredPeriod,
                }))
            );
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t(
                        "ControlActivitiesList_CoveredPeriodFilter_Placeholder"
                    )}
                    value={value}
                    setFilterValue={setFilterValue}
                    // Options are pre-sorted by period date (oldest first);
                    // don't let the select re-sort them alphabetically.
                    presorted
                />
            );
        }
        case COLUMN_ID.ENTITY: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const options = buildLatestEntitySnapshotOptions(
                column.getFacetedRowModel().flatRows
            ).map(({ entityId, name }) => ({
                label: name,
                value: entityId,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t(
                        "ControlActivitiesList_EntityFilter_Placeholder"
                    )}
                    dropdownMatchSelectWidth={500}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.WORKFLOW_STATUS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <StatusCascader
                    t={t}
                    value={value}
                    setFilterValue={setFilterValue}
                    placeholder={t(
                        "ControlActivitiesList_StatusFilter_Placeholder"
                    )}
                />
            );
        }
        case COLUMN_ID.HEALTH: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={buildHealthFilterOptions(t)}
                    // Reuses the column header copy ("Health") as the hint.
                    placeholder={t("ControlActivitiesList_Health_Header")}
                    value={value}
                    setFilterValue={setFilterValue}
                    // Keep the severity order (high friction → in progress).
                    presorted
                    {...pendo(CONTROL_ACTIVITIES_PENDO.healthFilter)}
                />
            );
        }
        case COLUMN_ID.FRICTION_TYPE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={[
                        {
                            value: FRICTION_TYPE_FILTER_KEYS.OVERDUE,
                            label: t("ControlResult_Health_Overdue"),
                        },
                        {
                            value: FRICTION_TYPE_FILTER_KEYS.REJECTED,
                            label: t("ControlResult_Health_Rejected"),
                        },
                        {
                            value: FRICTION_TYPE_FILTER_KEYS.REOPENED,
                            label: t("ControlResult_Health_Reopened"),
                        },
                    ]}
                    placeholder={t("ControlActivitiesList_FrictionType_Header")}
                    value={value}
                    setFilterValue={setFilterValue}
                    presorted
                />
            );
        }
        //not nullable date filters
        case COLUMN_ID.CREATED:
        case COLUMN_ID.COMPLETION_DUE_DATE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <FilterBarDateTimeRange
                    filterValue={value}
                    setFilterValue={setFilterValue}
                    facetedValues={column.getFacetedUniqueValues()}
                />
            );
        }
        case COLUMN_ID.TAGS: {
            const rows = column.getFacetedRowModel();
            const tagsPerRow = rows.flatRows.map((row) => row.original.tags);
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );

            return (
                <TSTValidStringTagsFilter
                    placeholder={t(
                        "ControlActivitiesList_TagFilter_Placeholder"
                    )}
                    tags={tagsPerRow}
                    setFilterValue={setFilterValue}
                    column={column}
                    value={value}
                />
            );
        }
        // nullable date filters
        case COLUMN_ID.FIRST_REVIEWER_DUE_DATE:
        case COLUMN_ID.SECOND_REVIEWER_DUE_DATE:
        case COLUMN_ID.COMPLETED_DATE:
        case COLUMN_ID.REVIEWED_DATE:
        case COLUMN_ID.FIRST_REVIEWED_DATE:
        case COLUMN_ID.SECOND_REVIEWED_DATE:
        case COLUMN_ID.COMPLETED_AND_REVIEWED_DATE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <FilterBarDateTimeRange
                    filterValue={value}
                    setFilterValue={setFilterValue}
                    facetedValues={column.getFacetedUniqueValues()}
                />
            );
        }
        case COLUMN_ID.RESPONSIBLE:
        case COLUMN_ID.CLAIMANT:
        case COLUMN_ID.PERFORMER:
        case COLUMN_ID.REVIEWER:
        case COLUMN_ID.REVIEW_PERFORMER:
        case COLUMN_ID.SECOND_REVIEWER:
        case COLUMN_ID.SECOND_REVIEW_PERFORMER: {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = values
                .keys()
                .map((title) => ({
                    label: title,
                    value: title,
                }))
                .toArray();
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={localizeColumnFilterPlaceholder(columnId, t)}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }

        case COLUMN_ID.RESPONSIBLE_OR_REVIEWER: {
            // The column value is a comma-joined string of multiple users, so
            // the faceted unique values would be whole combos. Build the
            // options from the individual users instead.
            const rows = column.getFacetedRowModel();
            const uniqueUsers = new Set<string>();
            for (const row of rows.flatRows) {
                for (const name of getResponsibleOrReviewerNames(
                    row.original,
                    t
                )) {
                    uniqueUsers.add(name);
                }
            }
            const options = [...uniqueUsers].sort().map((name) => ({
                label: name,
                value: name,
            }));
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t(
                        "ControlActivitiesList_ResponsibleOrReviewerFilter_Placeholder"
                    )}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.CLASSIFICATION_CLASS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const options = buildClassificationFilterOptions(t, column);
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t(
                        "ControlActivitiesList_ClassificationFilter_Placeholder"
                    )}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.DESCRIPTION:
            // Contains-only column: the string modes render the shared text
            // input, so there is no set-family filter component to build.
            return null;

        case COLUMN_ID.DUE_DATE: // filtered by the backend
        case COLUMN_ID.ARCHIVED: // filtered by the backend
            throw new Error("Filtering is not supported yet");

        default:
            assertNever(columnId);
    }
}

/**
 * Real classes sort alphabetically; the two pseudo-classes are pinned to the end
 * so they read as the special cases they are rather than being mixed in.
 */
function buildClassificationFilterOptions(
    t: TFunction,
    column: Column<ControlActivityTableRow>
): Array<{ label: string; value: string }> {
    const present = new Set<string>();
    for (const row of column.getFacetedRowModel().flatRows) {
        for (const className of row.original.classificationClass) {
            present.add(className);
        }
    }

    const realClasses = Array.from(present)
        .filter((c) => !PSEUDO_CLASS_TOKENS.includes(c))
        .sort();
    const pseudoClasses = PSEUDO_CLASS_TOKENS.filter((c) => present.has(c));

    return realClasses.concat(pseudoClasses).map((className) => ({
        label: localizeClassificationClass(className, t),
        value: className,
    }));
}
